import React, { useId } from 'react';
import { cn } from '../../utils/cn';

/**
 * Multi-line text, matching `Input`.
 *
 * Every textarea in the app was hand-rolled: its own label markup, its own
 * border classes, its own error paragraph, and — in every case — no
 * `aria-describedby`. So a screen-reader user heard the label and then an empty
 * box, with the hint and the validation error visible on screen but never
 * announced. That's the same gap `Input` was fixed for; this brings textareas
 * to the same standard.
 *
 * `maxLength` shows a live counter, and the counter turns to error colour as it
 * approaches the limit rather than at it — a warning after you've been
 * truncated is not a warning.
 */
const Textarea = React.forwardRef(({
  className,
  label,
  description,
  error,
  required = false,
  id,
  rows = 5,
  maxLength,
  value = '',
  ...props
}, ref) => {
  const generatedId = useId();
  const fieldId = id || `textarea-${generatedId}`;

  const describedBy = [
    description && !error ? `${fieldId}-description` : null,
    error ? `${fieldId}-error` : null,
    maxLength ? `${fieldId}-count` : null,
  ].filter(Boolean).join(' ') || undefined;

  const length = String(value ?? '').length;
  const nearLimit = maxLength ? length > maxLength * 0.9 : false;

  return (
    <div className="space-y-1.5">
      {label && (
        <label
          htmlFor={fieldId}
          className={cn('text-xs font-medium block', error ? 'text-error' : 'text-foreground')}
        >
          {label}
          {required && <span className="text-error ms-0.5">*</span>}
        </label>
      )}

      <textarea
        ref={ref}
        id={fieldId}
        rows={rows}
        value={value}
        maxLength={maxLength}
        aria-describedby={describedBy}
        aria-invalid={error ? true : undefined}
        aria-required={required || undefined}
        className={cn(
          'w-full rounded-lg border bg-background px-3 py-2 text-sm text-foreground placeholder:text-muted-foreground',
          'focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary shadow-sm',
          'transition-all duration-200 resize-y',
          error ? 'border-error' : 'border-input',
          className
        )}
        {...props}
      />

      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          {description && !error && (
            <p id={`${fieldId}-description`} className="text-xs text-muted-foreground">
              {description}
            </p>
          )}
          {error && (
            <p id={`${fieldId}-error`} className="text-xs text-error" role="alert">
              {error}
            </p>
          )}
        </div>
        {maxLength && (
          <p
            id={`${fieldId}-count`}
            className={cn(
              'text-[11px] tabular-nums flex-shrink-0',
              nearLimit ? 'text-error' : 'text-muted-foreground'
            )}
          >
            {length}/{maxLength}
          </p>
        )}
      </div>
    </div>
  );
});

Textarea.displayName = 'Textarea';

export default Textarea;
